import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  gameIconPlaceholder,
  markGameIconFailed,
  markGameIconLoaded,
  sideGameImageCandidates,
} from '../../data/casinoGames';
import { casinoRouteForLobby } from '../../data/casinoData';
import { useCasinoGameFeed } from '../../hooks/useCasinoGameFeed';
import { formatProviderDisplayName } from '../../utils/gameCardTheme';
import './home-gh-lobby-rail.css';

const PAGE = 36;

const TABS_BY_LOBBY = {
  slots: [
    { id: 'popular', label: 'Popüler', title: 'Popüler Oyunlar', category: 'all', sort: 'popular' },
    { id: 'new', label: 'Yeni', title: 'Yeni Oyunlar', category: 'new', sort: 'popular' },
    { id: 'vip-egt', label: 'EGT VIP', title: 'EGT VIP', category: 'vip-egt', sort: 'popular' },
    { id: 'amusnet', label: 'Amusnet', title: 'Amusnet', category: 'amusnet', sort: 'popular' },
  ],
  live: [
    { id: 'popular', label: 'Popüler', title: 'Popüler Oyunlar', category: 'all', sort: 'popular' },
    { id: 'new', label: 'Yeni', title: 'Yeni Oyunlar', category: 'new', sort: 'popular' },
    { id: 'roulette', label: 'Rulet', title: 'Rulet', category: 'roulette', sort: 'popular' },
    { id: 'blackjack', label: 'Blackjack', title: 'Blackjack', category: 'blackjack', sort: 'popular' },
    { id: 'baccarat', label: 'Baccarat', title: 'Baccarat', category: 'baccarat', sort: 'popular' },
    { id: 'game-shows', label: 'Game Shows', title: 'Game Shows', category: 'game-shows', sort: 'popular' },
  ],
};

const LOBBY_TITLE = {
  slots: 'Casino',
  live: 'Canlı Casino',
};

function GameCard({ game, onOpen, eager }) {
  const [srcIndex, setSrcIndex] = useState(0);
  const [failed, setFailed] = useState(false);
  const candidates = useMemo(
    () => sideGameImageCandidates(game, { size: 'md', preferLandscape: false }),
    [game],
  );
  const src = (!failed && candidates[srcIndex]) || gameIconPlaceholder(game);
  const name = game?.name || game?.title || '';
  const provider = formatProviderDisplayName(game?.provider || game?.vendor || '');

  useEffect(() => {
    setSrcIndex(0);
    setFailed(false);
  }, [game?.id]);

  return (
    <button
      type="button"
      className="home-rail-card home-gh-rail__card"
      aria-label={name}
      onClick={() => onOpen(game)}
    >
      <span className="home-rail-card__hit">
        <img
          className="home-rail-card__art"
          src={src}
          alt=""
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          draggable={false}
          onLoad={() => markGameIconLoaded(src)}
          onError={() => {
            markGameIconFailed(src);
            if (srcIndex + 1 < candidates.length) setSrcIndex((i) => i + 1);
            else setFailed(true);
          }}
        />
        <span className="home-rail-card__shade" aria-hidden="true" />
        <span className="home-rail-card__overlay">
          <span className="home-rail-card__play">
            <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
              <path
                fill="currentColor"
                fillRule="evenodd"
                d="M13.3084 8.07856C13.9482 8.52642 13.9482 9.47397 13.3084 9.92183L7.39514 14.0611C6.64952 14.583 5.625 14.0496 5.625 13.1395V4.86093C5.625 3.95078 6.64952 3.41736 7.39515 3.9393L13.3084 8.07856Z"
              />
            </svg>
            Oyna
          </span>
        </span>
      </span>
      <span className="home-rail-card__meta">
        <span className="home-rail-card__name">{name}</span>
        <span className="home-rail-card__prov">{provider}</span>
      </span>
    </button>
  );
}

function SkeletonRail({ count = 10 }) {
  return (
    <div className="home-gh-rail__track home-gh-rail__track--skel" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="home-gh-rail__skel" />
      ))}
    </div>
  );
}

export default function HomeGhLobbyRail({ lobby = 'slots', onNavigate }) {
  const lobbyId = TABS_BY_LOBBY[lobby] ? lobby : 'slots';
  const tabs = TABS_BY_LOBBY[lobbyId];
  const [tabId, setTabId] = useState(tabs[0].id);
  const activeTab = tabs.find((t) => t.id === tabId) || tabs[0];
  const trackRef = useRef(null);

  const feed = useCasinoGameFeed({
    lobby: lobbyId,
    category: activeTab.category,
    sort: activeTab.sort || 'popular',
    pageSize: PAGE,
    enabled: true,
  });

  const games = useMemo(
    () => (Array.isArray(feed.games) ? feed.games.slice(0, PAGE) : []),
    [feed.games],
  );

  const openGame = useCallback((game) => {
    const id = game?.id ?? game?.slug;
    if (!id) return;
    onNavigate?.('casino', { casinoGameId: String(id) });
  }, [onNavigate]);

  const openLobby = useCallback((e) => {
    e?.preventDefault?.();
    onNavigate?.('casino', casinoRouteForLobby(lobbyId));
  }, [lobbyId, onNavigate]);

  useEffect(() => {
    const el = trackRef.current;
    if (el) el.scrollLeft = 0;
  }, [tabId, lobbyId]);

  const title = LOBBY_TITLE[lobbyId] || activeTab.title;
  const loading = feed.loading && !games.length;

  return (
    <section className={`home-gh-rail home-gh-rail--${lobbyId}`} aria-label={title}>
      <div className="home-gh-rail__head">
        <div className="home-gh-rail__head-left">
          <h2>{title}</h2>
        </div>
        <button type="button" className="home-gh-rail__all" onClick={openLobby}>
          Tümünü göster
        </button>
      </div>

      <div className="home-gh-rail__tabs" role="tablist" aria-label={title}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={tabId === tab.id}
            className={`home-gh-rail__chip${tabId === tab.id ? ' is-active' : ''}`}
            onClick={() => setTabId(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <SkeletonRail />
      ) : (
        <div className="home-gh-rail__track" ref={trackRef}>
          {games.map((game, index) => (
            <GameCard
              key={game.id ?? `${game.name}-${index}`}
              game={game}
              onOpen={openGame}
              eager={index < 8}
            />
          ))}
        </div>
      )}

      {!loading && games.length === 0 ? (
        <p className="home-gh-rail__empty">Oyun bulunamadı</p>
      ) : null}
    </section>
  );
}
